import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/**
 * M25 L25.05 — Human-in-the-loop review queue (BR-06).
 *
 * Low-confidence predictions are flagged `isLowConfidence=true` and await human
 * review. This screen lists that queue via `GET /v1/ai/predictions?isLowConfidence=true`
 * and links each row to the explainability + action screen.
 */
interface QueueItem {
  readonly predictionId: string;
  readonly predictionType: string;
  readonly entityType: string;
  readonly entityId: string | null;
  /** The record it is about, named from its master (G18). */
  readonly subjectCode: string | null;
  readonly subjectName: string | null;
  readonly predictedLabel: string | null;
  readonly predictedValue: string | null;
  readonly confidenceScore: string;
  readonly riskLevel: string | null;
  readonly status: string;
  readonly predictedAt: string;
}

export function HitlQueuePage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<QueueItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    setError(null);
    try {
      const r = await apiRequest<{ data: QueueItem[]; total?: number }>(
        '/v1/ai/predictions?isLowConfidence=true&limit=50', { token },
      );
      setRows(r.data);
      setTotal(r.total ?? r.data.length);
    } catch (e) { setError((e as Error).message); }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="hitl-queue">
      <h1 style={{ fontSize: 20, margin: '0 0 4px' }}>{t('m25.hitl.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 0 }}>
        {t('m25.hitl.subtitle')}
        {rows && <span data-testid="queue-count"> · {total}</span>}
      </p>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        !error && <p>{t('m25.hitl.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="queue-empty">{t('m25.hitl.empty')}</p>
      ) : (
        <table aria-label="queue-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--color-border-default)' }}>
              <th style={{ padding: 8 }}>{t('m25.hitl.colType')}</th>
              <th style={{ padding: 8 }}>{t('m25.hitl.colEntity')}</th>
              <th style={{ padding: 8 }}>{t('m25.hitl.colPrediction')}</th>
              <th style={{ padding: 8 }}>{t('m25.hitl.colConfidence')}</th>
              <th style={{ padding: 8 }}>{t('m25.hitl.colRisk')}</th>
              <th style={{ padding: 8 }}>{t('m25.hitl.colStatus')}</th>
              <th style={{ padding: 8 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.predictionId} data-testid={`queue-row-${r.predictionId}`} style={{ borderBottom: '1px solid var(--color-bg-muted)' }}>
                <td style={{ padding: 8 }}>{r.predictionType}</td>
                <td style={{ padding: 8 }}>{r.entityType}{r.subjectCode ? ` · ${r.subjectCode} — ${r.subjectName ?? ''}` : ''}</td>
                <td style={{ padding: 8 }}>{r.predictedLabel ?? r.predictedValue ?? '—'}</td>
                <td style={{ padding: 8 }}>{decimal(r.confidenceScore)}</td>
                <td style={{ padding: 8 }}>{r.riskLevel ?? '—'}</td>
                <td style={{ padding: 8 }}>{r.status}</td>
                <td style={{ padding: 8 }}>
                  <Link to={`/admin/ml/explain/${r.predictionId}`} data-testid={`review-${r.predictionId}`}>{t('m25.hitl.review')}</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
